import React from "react";
import { useCallback, useEffect, useRef } from "react";
import { Coordinate } from "ol/coordinate";
import Feature from "ol/Feature";
import { ANNOTATION_COLOR } from "../../constants/color";
import { useMap } from "../../hooks/incontext/useMap";
import { MultiPoint } from "ol/geom";
import { fromLonLat } from "ol/proj";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { Circle, Fill, Stroke, Style } from "ol/style";
import { makeText } from "../../utils/object";
import { Annotation } from ".";
import { useInteractionEvent } from "../../hooks/incontext/useInteractionEvent";
import { Geometry } from "ol/geom";

export interface CustomMultiPointProps extends Annotation {
  positions: Coordinate[];
}

export const CustomMultiPoint = ({
  positions,
  color = "BLUE",
  properties = {},
  onClick,
  onHover,
  onLeave,
  zIndex = 0,
  children,
  opacity = 1,
  isDisabledSelection = false,
}: CustomMultiPointProps) => {
  const map = useMap();
  const annotationRef = useRef<Feature<MultiPoint>>(
    new Feature(
      new MultiPoint(positions.map((position) => fromLonLat(position)))
    )
  );

  const annotationLayerRef = useRef<
    VectorLayer<VectorSource<Feature<Geometry>>>
  >(
    new VectorLayer({
      source: new VectorSource({
        wrapX: false,
        features: [annotationRef.current],
      }),
    })
  );

  const onHoverHandler = useCallback(
    (feature: Feature) => {
      if (feature) {
        if (onHover) {
          onHover({ annotation: annotationRef.current, properties });
        }
      }
    },
    [onHover, properties]
  );

  const onClickHandler = useCallback(() => {
    if (onClick) {
      onClick({
        annotation: annotationRef.current,
        properties,
      });
    }
  }, [onClick, properties]);

  useInteractionEvent({
    annotation: annotationLayerRef.current,
    onClick: onClickHandler,
    onHover: onHoverHandler,
    isDisabledSelection,
  });

  useEffect(() => {
    if (annotationLayerRef.current) {
      annotationLayerRef.current.setZIndex(zIndex);
    }
  }, [zIndex]);

  useEffect(() => {
    const geometry = annotationRef.current.getGeometry() as MultiPoint;
    const vectorSource = annotationLayerRef.current.getSource() as VectorSource<
      Feature<Geometry>
    >;

    const points = geometry.getPoints().map((point, index) => {
      const text = index + 1; // 순번 설정
      const style = new Style({
        image: new Circle({
          radius: 10,
          fill: new Fill({
            color: ANNOTATION_COLOR[color].fill(opacity), // 원의 색상
          }),
          stroke: new Stroke({
            color: ANNOTATION_COLOR[color].stroke(opacity), // 테두리 선의 색상
            width: 2,
          }),
        }),
        text: makeText({
          text: String(text),
          size: children?.props.size || 15,
          color: children?.props.color ? children.props.color : "black",
          outline: children?.props.outline,
          isMarker: false,
        }),
      });
      const textStyle = style.getText();
      if (textStyle) {
        textStyle.setText(text.toString());
      }
      const pointFeature = new Feature(point);
      pointFeature.setStyle(style);
      pointFeature.setProperties({
        ...properties,
        shape: "MultiPoint",
        isModifying: false,
        annotation: annotationRef.current,
        source: annotationLayerRef.current.getSource(),
        layer: annotationLayerRef.current,
        hasPopup: children?.props.isPopup,
      });
      return pointFeature;
    });

    if (vectorSource) {
      vectorSource.addFeatures(points);
    }

    map.addLayer(annotationLayerRef.current);

    return () => {
      map.removeLayer(annotationLayerRef.current);
      annotationLayerRef.current.getSource()?.clear();
    };
  }, [color, children, map, opacity, properties]);
  return null;
};
